/**
 * 终极JSON解析器 - 彻底解决AI生成JSON的解析问题
 * 
 * 问题根源分析：
 * 1. 现有的fixChineseQuotes函数逻辑有严重缺陷，会错误转义正常的JSON属性名引号
 * 2. 多层修复逻辑相互干扰，导致越修复越错
 * 3. 缺乏对JSON结构的正确理解，盲目进行字符替换
 * 
 * 解决方案：
 * 1. 重新设计引号处理逻辑，正确识别JSON结构
 * 2. 采用状态机方式解析，避免误判
 * 3. 分层处理：先修复明显错误，再进行结构修复
 */

export class UltimateJsonParser {
  
  /**
   * 主要解析入口 - 一次性解决所有JSON解析问题
   */
  static parseAIResponse(response) {
    if (!response || typeof response !== 'string') {
      console.error('❌ 无效的响应内容')
      return null
    }
    
    console.log('🚀 启动终极JSON解析器')
    console.log('📄 原始响应长度:', response.length)
    console.log('📄 原始响应前200字符:', response.substring(0, 200))
    
    // 第1步：提取JSON内容
    const extracted = this.extractJsonContent(response)
    if (!extracted) {
      console.error('❌ 无法提取JSON内容')
      return null
    }
    
    console.log('✅ JSON内容提取成功，长度:', extracted.length)
    console.log('📄 提取内容前200字符:', extracted.substring(0, 200))
    
    // 第2步：智能修复并解析
    const parsed = this.smartRepairAndParse(extracted)
    if (parsed) {
      console.log('🎉 JSON解析成功！')
      return parsed
    }
    
    console.error('❌ 所有解析方法都失败了')
    return null
  }
  
  /**
   * 提取JSON内容 - 支持多种格式
   */
  static extractJsonContent(response) {
    const methods = [
      () => this.extractFromCodeBlock(response),
      () => this.extractFromMarkers(response),
      () => this.extractFromBoundaries(response),
      () => this.extractFromPattern(response)
    ]
    
    for (const method of methods) {
      const result = method()
      if (result) {
        console.log('✅ JSON内容提取成功')
        return result
      }
    }
    
    return null
  }
  
  /**
   * 从代码块中提取JSON
   */
  static extractFromCodeBlock(response) {
    // 方法1：```json 代码块
    let match = response.match(/```json\s*([\s\S]*?)\s*```/)
    if (match) {
      console.log('📝 从```json代码块提取')
      return match[1].trim()
    }
    
    // 方法2：``` 代码块
    match = response.match(/```\s*([\s\S]*?)\s*```/)
    if (match) {
      let content = match[1].trim()
      // 移除可能的语言标识
      content = content.replace(/^(json|javascript|js)\s+/i, '')
      console.log('📝 从```代码块提取')
      return content
    }
    
    return null
  }
  
  /**
   * 从特殊标记中提取JSON
   */
  static extractFromMarkers(response) {
    const markers = [
      { start: '===GENRE_OPTIONS_JSON_START===', end: '===GENRE_OPTIONS_JSON_END===' },
      { start: '===THEME_OPTIONS_JSON_START===', end: '===THEME_OPTIONS_JSON_END===' },
      { start: '===STYLE_OPTIONS_JSON_START===', end: '===STYLE_OPTIONS_JSON_END===' },
      { start: '===OUTLINE_OPTIONS_JSON_START===', end: '===OUTLINE_OPTIONS_JSON_END===' },
      { start: '===CHARACTERS_JSON_START===', end: '===CHARACTERS_JSON_END===' },
      { start: '===SCENES_JSON_START===', end: '===SCENES_JSON_END===' },
      { start: '===STORYBOARD_JSON_START===', end: '===STORYBOARD_JSON_END===' }
    ]
    
    for (const marker of markers) {
      const startIdx = response.indexOf(marker.start)
      const endIdx = response.indexOf(marker.end)
      
      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        const content = response.substring(startIdx + marker.start.length, endIdx).trim()
        console.log('📝 从特殊标记提取:', marker.start)
        return content
      }
    }
    
    return null
  }
  
  /**
   * 从JSON边界提取
   */
  static extractFromBoundaries(response) {
    // 查找第一个 { 或 [
    const firstBrace = response.indexOf('{')
    const firstBracket = response.indexOf('[')
    
    let startPos = -1
    let endChar = ''
    
    if (firstBrace !== -1 && firstBracket !== -1) {
      if (firstBrace < firstBracket) {
        startPos = firstBrace
        endChar = '}'
      } else {
        startPos = firstBracket
        endChar = ']'
      }
    } else if (firstBrace !== -1) {
      startPos = firstBrace
      endChar = '}'
    } else if (firstBracket !== -1) {
      startPos = firstBracket
      endChar = ']'
    }
    
    if (startPos === -1) {
      return null
    }
    
    // 查找对应的结束位置
    const endPos = response.lastIndexOf(endChar)
    if (endPos !== -1 && endPos > startPos) {
      const content = response.substring(startPos, endPos + 1)
      console.log('📝 从JSON边界提取')
      return content
    }
    
    return null
  }
  
  /**
   * 从模式匹配提取
   */
  static extractFromPattern(response) {
    // 尝试匹配完整的JSON对象
    let match = response.match(/\{[\s\S]*\}/)
    if (match) {
      console.log('📝 从对象模式提取')
      return match[0]
    }
    
    // 尝试匹配完整的JSON数组
    match = response.match(/\[[\s\S]*\]/)
    if (match) {
      console.log('📝 从数组模式提取')
      return match[0]
    }
    
    return null
  }
  
  /**
   * 智能修复并解析JSON
   */
  static smartRepairAndParse(jsonText) {
    console.log('🔧 开始智能修复...')
    
    // 检查是否是大型JSON，如果是，使用特殊处理
    if (jsonText.length > 20000) {
      console.log('📊 检测到大型JSON，使用特殊处理流程')
      const largeJsonResult = this.handleLargeJson(jsonText)
      if (largeJsonResult) return largeJsonResult
    }
    
    // 尝试直接解析（无修复）
    let result = this.tryParse(jsonText, '直接解析')
    if (result) return result
    
    // 第1级修复：基础清理
    let cleaned = this.basicClean(jsonText)
    result = this.tryParse(cleaned, '基础清理')
    if (result) return result
    
    // 第2级修复：引号修复
    let quotesFixed = this.fixQuotes(cleaned)
    result = this.tryParse(quotesFixed, '引号修复')
    if (result) return result
    
    // 第3级修复：结构修复
    let structureFixed = this.fixStructure(quotesFixed)
    result = this.tryParse(structureFixed, '结构修复')
    if (result) return result
    
    // 第4级修复：深度修复（对大型JSON更保守）
    let deepFixed = jsonText.length > 10000 ? 
      this.conservativeRepair(structureFixed) : 
      this.deepRepair(structureFixed)
    result = this.tryParse(deepFixed, '深度修复')
    if (result) return result
    
    // 第5级修复：部分提取
    result = this.extractPartialJson(jsonText)
    if (result) {
      console.log('✅ 部分JSON提取成功')
      return result
    }
    
    return null
  }
  
  /**
   * 处理大型JSON的特殊逻辑
   */
  static handleLargeJson(jsonText) {
    console.log('🔧 处理大型JSON...')
    
    // 首先尝试找到完整的JSON边界
    const truncated = this.findValidJsonBoundary(jsonText)
    if (truncated && truncated !== jsonText) {
      console.log(`📏 截断大型JSON: ${jsonText.length} -> ${truncated.length}`)
      const result = this.tryParse(truncated, '大型JSON截断')
      if (result) return result
    }
    
    // 如果截断失败，尝试分段解析
    return this.parseJsonInChunks(jsonText)
  }
  
  /**
   * 找到有效的JSON边界
   */
  static findValidJsonBoundary(text) {
    // 检查是否以 { 开头
    if (text.trim().startsWith('{')) {
      return this.findLastCompleteObject(text)
    }
    
    // 检查是否以 [ 开头
    if (text.trim().startsWith('[')) {
      return this.findLastCompleteArray(text)
    }
    
    return text
  }
  
  /**
   * 分段解析JSON
   */
  static parseJsonInChunks(text) {
    console.log('🔧 尝试分段解析JSON...')
    
    // 如果是数组，尝试解析到最后一个完整的元素
    if (text.trim().startsWith('[')) {
      return this.parseArrayInChunks(text)
    }
    
    // 如果是对象，尝试解析到最后一个完整的属性
    if (text.trim().startsWith('{')) {
      return this.parseObjectInChunks(text)
    }
    
    return null
  }
  
  /**
   * 分段解析数组
   */
  static parseArrayInChunks(text) {
    const elements = []
    let depth = 0
    let inString = false
    let escapeNext = false
    let currentElement = ''
    let elementStart = -1
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      
      if (escapeNext) {
        currentElement += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        currentElement += char
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        inString = !inString
        currentElement += char
        continue
      }
      
      if (!inString) {
        if (char === '[') {
          if (depth === 0) {
            elementStart = i + 1
          } else {
            currentElement += char
          }
          depth++
        } else if (char === '{') {
          if (depth === 1 && elementStart !== -1) {
            currentElement = '{'
          } else {
            currentElement += char
          }
          depth++
        } else if (char === '}') {
          depth--
          currentElement += char
          if (depth === 1) {
            // 尝试解析这个元素
            try {
              const parsed = JSON.parse(currentElement)
              elements.push(parsed)
              console.log(`  ✅ 解析元素成功: ${elements.length}`)
            } catch (e) {
              console.log(`  ⚠️ 元素解析失败，停止: ${e.message}`)
              break
            }
            currentElement = ''
          }
        } else if (char === ']') {
          depth--
          if (depth === 0) {
            break
          } else {
            currentElement += char
          }
        } else if (char === ',' && depth === 1) {
          if (currentElement.trim()) {
            try {
              const parsed = JSON.parse(currentElement.trim())
              elements.push(parsed)
              console.log(`  ✅ 解析元素成功: ${elements.length}`)
            } catch (e) {
              console.log(`  ⚠️ 元素解析失败，停止: ${e.message}`)
              break
            }
          }
          currentElement = ''
        } else if (depth > 0) {
          currentElement += char
        }
      } else {
        currentElement += char
      }
    }
    
    if (elements.length > 0) {
      console.log(`✅ 分段解析成功，获得 ${elements.length} 个元素`)
      return elements
    }
    
    return null
  }
  
  /**
   * 分段解析对象
   */
  static parseObjectInChunks(text) {
    // 尝试找到最后一个完整的属性
    const lastCommaIndex = text.lastIndexOf(',')
    if (lastCommaIndex !== -1) {
      const truncated = text.substring(0, lastCommaIndex) + '}'
      try {
        const parsed = JSON.parse(truncated)
        console.log('✅ 对象分段解析成功')
        return parsed
      } catch (e) {
        // 继续尝试更短的版本
      }
    }
    
    return null
  }
  
  /**
   * 保守修复（用于大型JSON）
   */
  static conservativeRepair(text) {
    console.log('🔧 执行保守修复（大型JSON模式）...')
    
    let fixed = text
    
    // 只做最基本的修复
    // 移除尾随逗号
    fixed = fixed.replace(/,(\s*[}\]])/g, '$1')
    
    // 修复明显的括号不匹配（但更保守）
    const analysis = this.analyzeJsonStructure(fixed)
    if (analysis.braceDiff > 0 && analysis.braceDiff <= 2) {
      fixed += '}'.repeat(analysis.braceDiff)
      console.log(`  保守补充了 ${analysis.braceDiff} 个闭大括号`)
    }
    
    if (analysis.bracketDiff > 0 && analysis.bracketDiff <= 2) {
      fixed += ']'.repeat(analysis.bracketDiff)
      console.log(`  保守补充了 ${analysis.bracketDiff} 个闭方括号`)
    }
    
    console.log('✅ 保守修复完成')
    return fixed
  }
  
  /**
   * 尝试解析JSON
   */
  static tryParse(jsonText, method) {
    try {
      const parsed = JSON.parse(jsonText)
      console.log(`✅ ${method}解析成功`)
      return parsed
    } catch (error) {
      console.log(`❌ ${method}解析失败:`, error.message)
      
      // 分析错误位置
      const positionMatch = error.message.match(/position (\d+)/)
      if (positionMatch) {
        const position = parseInt(positionMatch[1])
        const context = jsonText.substring(Math.max(0, position - 10), position + 10)
        console.log(`   错误位置 ${position}:`, context)
        console.log(`   错误字符:`, jsonText[position] || 'EOF')
      }
      
      return null
    }
  }
  
  /**
   * 基础清理
   */
  static basicClean(text) {
    console.log('🧹 执行基础清理...')
    
    let cleaned = text.trim()
    
    // 移除BOM
    if (cleaned.charCodeAt(0) === 0xFEFF) {
      cleaned = cleaned.substring(1)
      console.log('  移除BOM')
    }
    
    // 移除前后的非JSON字符
    cleaned = cleaned.replace(/^[^\[{]*/, '').replace(/[^\]}]*$/, '')
    
    // 移除注释
    cleaned = cleaned.replace(/\/\*[\s\S]*?\*\//g, '')
    cleaned = cleaned.replace(/\/\/.*$/gm, '')
    
    console.log('✅ 基础清理完成')
    return cleaned
  }
  
  /**
   * 修复引号问题 - 重新设计的正确逻辑
   */
  static fixQuotes(text) {
    console.log('🔧 执行引号修复...')
    
    // 第1步：替换中文引号为英文引号
    let fixed = text
      .replace(/"/g, '"')  // 左双引号
      .replace(/"/g, '"')  // 右双引号
      .replace(/'/g, "'")  // 左单引号
      .replace(/'/g, "'")  // 右单引号
      .replace(/＂/g, '"') // 全角双引号
      .replace(/＇/g, "'") // 全角单引号
    
    console.log('  第1步：中文引号替换完成')
    
    // 第2步：使用状态机正确处理引号
    fixed = this.processQuotesWithStateMachine(fixed)
    
    console.log('✅ 引号修复完成')
    return fixed
  }
  
  /**
   * 使用状态机处理引号 - 正确的方法
   */
  static processQuotesWithStateMachine(text) {
    console.log('🤖 使用状态机处理引号...')
    
    // 简化方法：直接处理字符串内部的引号转义
    let result = ''
    let inString = false
    let escapeNext = false
    let stringStart = -1
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      
      // 处理转义
      if (escapeNext) {
        result += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        result += char
        escapeNext = true
        continue
      }
      
      // 处理引号
      if (char === '"') {
        if (!inString) {
          // 字符串开始
          inString = true
          stringStart = i
          result += char
        } else {
          // 字符串可能结束，检查后面的字符
          const nextNonSpace = this.getNextNonSpaceChar(text, i + 1)
          
          // 如果后面是 : , } ] 或文件结束，则是字符串结束
          if (nextNonSpace === ':' || nextNonSpace === ',' || 
              nextNonSpace === '}' || nextNonSpace === ']' || 
              nextNonSpace === null) {
            // 字符串结束
            inString = false
            result += char
          } else {
            // 字符串内部的引号，需要转义
            result += '\\"'
          }
        }
      } else {
        result += char
      }
    }
    
    console.log('✅ 状态机处理完成')
    return result
  }
  
  /**
   * 获取下一个非空白字符
   */
  static getNextNonSpaceChar(text, startIndex) {
    for (let i = startIndex; i < text.length; i++) {
      const char = text[i]
      if (char !== ' ' && char !== '\t' && char !== '\n' && char !== '\r') {
        return char
      }
    }
    return null
  }
  
  /**
   * 查找匹配的引号
   */
  static findMatchingQuote(text, startIndex) {
    let escapeNext = false
    
    for (let i = startIndex; i < text.length; i++) {
      const char = text[i]
      
      if (escapeNext) {
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        return i
      }
    }
    
    return -1
  }
  
  /**
   * 修复JSON结构
   */
  static fixStructure(text) {
    console.log('🔧 执行结构修复...')
    
    let fixed = text
    
    // 修复常见语法错误
    fixed = fixed.replace(/,(\s*[}\]])/g, '$1')  // 移除尾随逗号
    fixed = fixed.replace(/([}\]])(\s*)([{\[])/g, '$1,$2$3')  // 添加缺失的逗号
    
    // 修复属性名
    fixed = fixed.replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
    
    // 修复单引号字符串
    fixed = fixed.replace(/:\s*'([^']*)'/g, ': "$1"')
    
    // 修复括号匹配
    fixed = this.fixBrackets(fixed)
    
    console.log('✅ 结构修复完成')
    return fixed
  }
  
  /**
   * 智能修复括号匹配 - 避免过度修复
   */
  static fixBrackets(text) {
    console.log('🔧 开始智能括号修复...')
    
    // 首先分析JSON的实际结构
    const analysis = this.analyzeJsonStructure(text)
    console.log('📊 JSON结构分析:', analysis)
    
    let fixed = text
    
    // 只有在差异合理的情况下才进行修复
    if (analysis.braceDiff > 0 && analysis.braceDiff <= 5) {
      // 检查是否真的需要补充括号
      if (this.shouldAddBraces(text, analysis.braceDiff)) {
        fixed += '}'.repeat(analysis.braceDiff)
        console.log(`  补充了 ${analysis.braceDiff} 个闭大括号`)
      } else {
        console.log('  跳过大括号补充（可能导致过度修复）')
      }
    } else if (analysis.braceDiff > 5) {
      console.log('  大括号差异过大，尝试截断修复')
      fixed = this.truncateToValidJson(text, 'object')
    }
    
    if (analysis.bracketDiff > 0 && analysis.bracketDiff <= 5) {
      if (this.shouldAddBrackets(text, analysis.bracketDiff)) {
        fixed += ']'.repeat(analysis.bracketDiff)
        console.log(`  补充了 ${analysis.bracketDiff} 个闭方括号`)
      } else {
        console.log('  跳过方括号补充（可能导致过度修复）')
      }
    } else if (analysis.bracketDiff > 5) {
      console.log('  方括号差异过大，尝试截断修复')
      fixed = this.truncateToValidJson(text, 'array')
    }
    
    return fixed
  }
  
  /**
   * 分析JSON结构
   */
  static analyzeJsonStructure(text) {
    const openBraces = (text.match(/\{/g) || []).length
    const closeBraces = (text.match(/\}/g) || []).length
    const openBrackets = (text.match(/\[/g) || []).length
    const closeBrackets = (text.match(/\]/g) || []).length
    
    return {
      openBraces,
      closeBraces,
      openBrackets,
      closeBrackets,
      braceDiff: openBraces - closeBraces,
      bracketDiff: openBrackets - closeBrackets,
      totalLength: text.length,
      isLarge: text.length > 10000
    }
  }
  
  /**
   * 判断是否应该添加大括号
   */
  static shouldAddBraces(text, diff) {
    // 检查末尾是否已经有足够的闭括号
    const endingBraces = (text.match(/\}+$/)?.[0] || '').length
    if (endingBraces >= diff) {
      return false
    }
    
    // 检查是否在字符串内部（避免在字符串中添加括号）
    const lastChar = text.trim().slice(-1)
    if (lastChar === '"') {
      return false
    }
    
    // 检查最后的有效内容
    const trimmed = text.trim()
    const lastValidChar = trimmed.slice(-1)
    
    // 如果最后是逗号，说明可能有未完成的对象
    if (lastValidChar === ',') {
      return true
    }
    
    // 如果最后是有效的值结束符，可以添加
    return ['"', '}', ']', 'e', 'l', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(lastValidChar)
  }
  
  /**
   * 判断是否应该添加方括号
   */
  static shouldAddBrackets(text, diff) {
    const endingBrackets = (text.match(/\]+$/)?.[0] || '').length
    if (endingBrackets >= diff) {
      return false
    }
    
    const lastChar = text.trim().slice(-1)
    if (lastChar === '"') {
      return false
    }
    
    const trimmed = text.trim()
    const lastValidChar = trimmed.slice(-1)
    
    return ['"', '}', ']', 'e', 'l', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(lastValidChar)
  }
  
  /**
   * 截断到有效的JSON
   */
  static truncateToValidJson(text, expectedType) {
    console.log(`🔧 尝试截断到有效的${expectedType}...`)
    
    if (expectedType === 'object') {
      // 找到最后一个完整的对象
      return this.findLastCompleteObject(text)
    } else if (expectedType === 'array') {
      // 找到最后一个完整的数组元素
      return this.findLastCompleteArray(text)
    }
    
    return text
  }
  
  /**
   * 找到最后一个完整的对象
   */
  static findLastCompleteObject(text) {
    let depth = 0
    let lastCompletePos = -1
    let inString = false
    let escapeNext = false
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      
      if (escapeNext) {
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        inString = !inString
        continue
      }
      
      if (!inString) {
        if (char === '{') {
          depth++
        } else if (char === '}') {
          depth--
          if (depth === 0) {
            lastCompletePos = i
          }
        }
      }
    }
    
    if (lastCompletePos !== -1) {
      const truncated = text.substring(0, lastCompletePos + 1)
      console.log(`  截断到位置 ${lastCompletePos}，长度从 ${text.length} 减少到 ${truncated.length}`)
      return truncated
    }
    
    return text
  }
  
  /**
   * 找到最后一个完整的数组
   */
  static findLastCompleteArray(text) {
    let depth = 0
    let lastCompletePos = -1
    let inString = false
    let escapeNext = false
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      
      if (escapeNext) {
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        inString = !inString
        continue
      }
      
      if (!inString) {
        if (char === '[') {
          depth++
        } else if (char === ']') {
          depth--
          if (depth === 0) {
            lastCompletePos = i
          }
        }
      }
    }
    
    if (lastCompletePos !== -1) {
      const truncated = text.substring(0, lastCompletePos + 1)
      console.log(`  截断到位置 ${lastCompletePos}，长度从 ${text.length} 减少到 ${truncated.length}`)
      return truncated
    }
    
    return text
  }
  
  /**
   * 深度修复 - 处理复杂情况（更保守的方法）
   */
  static deepRepair(text) {
    console.log('🔧 执行深度修复...')
    
    let fixed = text
    
    // 只修复字符串内部的换行符，不修复JSON结构中的换行符
    fixed = this.fixStringNewlines(fixed)
    
    // 移除控制字符，但保留必要的空白字符
    fixed = fixed.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    
    // 修复明显的数字格式错误
    fixed = fixed.replace(/:\s*(\d+\.?\d*)\s*([,}\]])/g, ': $1$2')
    
    // 修复布尔值格式
    fixed = fixed.replace(/:\s*(true|false)\s*([,}\]])/g, ': $1$2')
    
    // 修复null值格式
    fixed = fixed.replace(/:\s*null\s*([,}\]])/g, ': null$1')
    
    console.log('✅ 深度修复完成')
    return fixed
  }
  
  /**
   * 修复字符串内部的换行符
   */
  static fixStringNewlines(text) {
    let result = ''
    let inString = false
    let escapeNext = false
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i]
      
      if (escapeNext) {
        result += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        result += char
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        inString = !inString
        result += char
        continue
      }
      
      // 只在字符串内部转义换行符
      if (inString) {
        if (char === '\n') {
          result += '\\n'
          continue
        }
        if (char === '\r') {
          result += '\\r'
          continue
        }
        if (char === '\t') {
          result += '\\t'
          continue
        }
      }
      
      result += char
    }
    
    return result
  }
  
  /**
   * 提取部分有效的JSON
   */
  static extractPartialJson(text) {
    console.log('🔧 尝试提取部分有效JSON...')
    
    // 如果是数组，尝试提取有效元素
    if (text.trim().startsWith('[')) {
      return this.extractValidArrayElements(text)
    }
    
    // 如果是对象，尝试提取有效属性
    if (text.trim().startsWith('{')) {
      return this.extractValidObjectProperties(text)
    }
    
    return null
  }
  
  /**
   * 提取有效的数组元素
   */
  static extractValidArrayElements(arrayText) {
    console.log('📦 提取数组元素...')
    
    const elements = []
    let depth = 0
    let inString = false
    let escapeNext = false
    let currentElement = ''
    let elementStart = -1
    
    for (let i = 0; i < arrayText.length; i++) {
      const char = arrayText[i]
      
      if (escapeNext) {
        currentElement += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        currentElement += char
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        inString = !inString
        currentElement += char
        continue
      }
      
      if (!inString) {
        if (char === '[') {
          if (depth === 0) {
            elementStart = i + 1
          } else {
            currentElement += char
          }
          depth++
        } else if (char === '{') {
          if (depth === 1 && elementStart !== -1) {
            currentElement = '{'
          } else {
            currentElement += char
          }
          depth++
        } else if (char === '}') {
          depth--
          currentElement += char
          if (depth === 1) {
            // 尝试解析这个元素
            try {
              const parsed = JSON.parse(currentElement)
              elements.push(parsed)
              console.log(`  ✅ 成功解析元素: ${currentElement.substring(0, 50)}...`)
            } catch (e) {
              console.log(`  ❌ 元素解析失败: ${e.message}`)
            }
            currentElement = ''
          }
        } else if (char === ']') {
          depth--
          if (depth === 0) {
            break
          } else {
            currentElement += char
          }
        } else if (char === ',' && depth === 1) {
          if (currentElement.trim()) {
            try {
              const parsed = JSON.parse(currentElement.trim())
              elements.push(parsed)
              console.log(`  ✅ 成功解析元素: ${currentElement.substring(0, 50)}...`)
            } catch (e) {
              console.log(`  ❌ 元素解析失败: ${e.message}`)
            }
          }
          currentElement = ''
        } else if (depth > 0) {
          currentElement += char
        }
      } else {
        currentElement += char
      }
    }
    
    if (elements.length > 0) {
      console.log(`✅ 成功提取 ${elements.length} 个数组元素`)
      return elements
    }
    
    return null
  }
  
  /**
   * 提取有效的对象属性
   */
  static extractValidObjectProperties(objectText) {
    console.log('📦 提取对象属性...')
    
    const obj = {}
    let depth = 0
    let inString = false
    let escapeNext = false
    let currentKey = ''
    let currentValue = ''
    let state = 'SEEKING_KEY'  // SEEKING_KEY, IN_KEY, SEEKING_COLON, SEEKING_VALUE, IN_VALUE
    
    for (let i = 0; i < objectText.length; i++) {
      const char = objectText[i]
      
      if (escapeNext) {
        if (state === 'IN_KEY') currentKey += char
        if (state === 'IN_VALUE') currentValue += char
        escapeNext = false
        continue
      }
      
      if (char === '\\') {
        if (state === 'IN_KEY') currentKey += char
        if (state === 'IN_VALUE') currentValue += char
        escapeNext = true
        continue
      }
      
      if (char === '"') {
        if (state === 'SEEKING_KEY') {
          state = 'IN_KEY'
          inString = true
        } else if (state === 'IN_KEY' && inString) {
          state = 'SEEKING_COLON'
          inString = false
        } else if (state === 'SEEKING_VALUE') {
          state = 'IN_VALUE'
          inString = true
          currentValue += char
        } else if (state === 'IN_VALUE' && inString) {
          currentValue += char
          inString = false
          // 尝试保存这个属性
          this.saveProperty(obj, currentKey, currentValue)
          currentKey = ''
          currentValue = ''
          state = 'SEEKING_KEY'
        } else if (state === 'IN_VALUE') {
          currentValue += char
        }
        continue
      }
      
      if (state === 'IN_KEY') {
        currentKey += char
      } else if (state === 'SEEKING_COLON' && char === ':') {
        state = 'SEEKING_VALUE'
      } else if (state === 'SEEKING_VALUE' && char.trim()) {
        if (char === '{' || char === '[') {
          depth++
          currentValue += char
          state = 'IN_VALUE'
        } else if (char !== ' ' && char !== '\t' && char !== '\n' && char !== '\r') {
          currentValue += char
          state = 'IN_VALUE'
        }
      } else if (state === 'IN_VALUE') {
        if (!inString) {
          if (char === '{' || char === '[') {
            depth++
          } else if (char === '}' || char === ']') {
            depth--
          }
          
          if ((char === ',' || char === '}') && depth === 0) {
            // 属性值结束
            this.saveProperty(obj, currentKey, currentValue)
            currentKey = ''
            currentValue = ''
            state = 'SEEKING_KEY'
            continue
          }
        }
        currentValue += char
      }
    }
    
    if (Object.keys(obj).length > 0) {
      console.log(`✅ 成功提取对象，包含 ${Object.keys(obj).length} 个属性`)
      return obj
    }
    
    return null
  }
  
  /**
   * 保存属性到对象
   */
  static saveProperty(obj, key, value) {
    if (!key.trim()) return
    
    try {
      const parsedValue = JSON.parse(value.trim())
      obj[key.trim()] = parsedValue
      console.log(`  ✅ 保存属性: ${key} = ${value.substring(0, 30)}...`)
    } catch (e) {
      // 如果解析失败，尝试作为字符串
      try {
        obj[key.trim()] = value.trim().replace(/^"(.*)"$/, '$1')
        console.log(`  ✅ 保存属性(字符串): ${key} = ${value.substring(0, 30)}...`)
      } catch (e2) {
        console.log(`  ❌ 属性保存失败: ${key} = ${value.substring(0, 30)}...`)
      }
    }
  }
}

export default UltimateJsonParser
